import { useMemo, useState } from 'react';
import type {
  CatalystPortalAccountMatch,
  CatalystPortalSyncResult,
  UpdateCatalystPortalScheduleInput,
} from '@catalyst-psa/shared';
import {
  CATALYST_PORTAL_DEFAULT_INTERVAL_MINUTES,
  CATALYST_PORTAL_MAX_INTERVAL_MINUTES,
  CATALYST_PORTAL_MIN_INTERVAL_MINUTES,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { TextField } from '../../components/ui/Field.js';
import {
  useCatalystPortalSchedule,
  useCatalystPortalSyncCandidates,
  useRunCatalystPortalSync,
  useUpdateCatalystPortalSchedule,
} from './api.js';

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function errorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return error instanceof ApiError ? error.message : fallback;
}

function ResultSummary({ result }: { result: CatalystPortalSyncResult }): JSX.Element {
  const tone = result.outcome === 'ok' ? 'green' : result.outcome === 'partial' ? 'amber' : 'red';
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone={tone}>{result.outcome}</Badge>
        {result.dryRun ? <Badge tone="blue">Preview only</Badge> : null}
        {result.scoped ? <Badge tone="gray">Selected records</Badge> : null}
        <span className="text-slate-600">
          {result.scanned} scanned · {result.matched} matched · {result.unmatched} not in portal ·{' '}
          {result.unchanged} already current ·{' '}
          <strong>
            {result.updated} {result.dryRun ? 'would change' : 'updated'}
          </strong>
        </span>
      </div>
      {result.message ? <p className="text-sm text-rose-600">{result.message}</p> : null}
      {result.changes.length ? (
        <ul className="max-h-48 space-y-1 overflow-auto text-xs text-slate-600">
          {result.changes.map((change) => (
            <li key={change.customerId}>
              <span className="font-medium text-slate-900">{change.customerName}</span>{' '}
              <span className="font-mono">{change.netsuiteAccountId}</span>:{' '}
              {change.currentAccountName ?? '—'} → {change.portalAccountName}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Schedules and runs the Catalyst Portal → PSA NetSuite account-name sync, and lets an admin
 * test the mapping against hand-picked customer records before running it for everyone.
 */
export function AccountNameSyncCard(): JSX.Element {
  const scheduleQuery = useCatalystPortalSchedule();
  const updateSchedule = useUpdateCatalystPortalSchedule();
  const runSync = useRunCatalystPortalSync();

  const schedule = scheduleQuery.data;
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [interval, setInterval] = useState<string | null>(null);
  const [scheduleSaved, setScheduleSaved] = useState(false);

  const [showTest, setShowTest] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const candidatesQuery = useCatalystPortalSyncCandidates(search, showTest);

  const effectiveEnabled = enabled ?? schedule?.enabled ?? false;
  const effectiveInterval =
    interval ??
    String(schedule?.intervalMinutes ?? CATALYST_PORTAL_DEFAULT_INTERVAL_MINUTES);

  const candidates = candidatesQuery.data ?? [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggle = (customerId: string, checked: boolean): void => {
    setSelected((current) =>
      checked ? [...current, customerId] : current.filter((id) => id !== customerId),
    );
  };

  const handleSaveSchedule = (): void => {
    setScheduleSaved(false);
    const parsed = Number(effectiveInterval);
    const input: UpdateCatalystPortalScheduleInput = {
      enabled: effectiveEnabled,
      intervalMinutes: Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null,
    };
    updateSchedule.mutate(input, {
      onSuccess: () => {
        setScheduleSaved(true);
        setEnabled(null);
        setInterval(null);
      },
    });
  };

  const scheduleError = errorMessage(updateSchedule.error, 'Could not save the schedule.');
  const runError = errorMessage(runSync.error, 'The sync failed.');
  const result = runSync.data;

  return (
    <Card>
      <div className="space-y-5">
        <div>
          <h2 className="text-base font-semibold text-slate-900">NetSuite account name sync</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Copies each account&apos;s NetSuite account name from the Catalyst Portal into the
            customer&apos;s <strong>NetSuite Account Name</strong> field (Other tab). Customers are
            matched on their PSA <strong>NetSuite Account ID</strong>; customers with no ID or no
            portal match are left untouched.
          </p>
        </div>

        <DataState
          isLoading={scheduleQuery.isLoading}
          isError={scheduleQuery.isError}
          error={scheduleQuery.error}
          onRetry={() => void scheduleQuery.refetch()}
          isEmpty={false}
        >
          {schedule ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Last run</div>
                  <div className="text-slate-900">{formatDateTime(schedule.lastRunAt)}</div>
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Next run</div>
                  <div className="text-slate-900">{formatDateTime(schedule.nextRunAt)}</div>
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Last outcome</div>
                  <div>
                    {schedule.lastOutcome ? (
                      <Badge
                        tone={
                          schedule.lastOutcome === 'ok'
                            ? 'green'
                            : schedule.lastOutcome === 'partial'
                              ? 'amber'
                              : 'red'
                        }
                      >
                        {schedule.lastOutcome}
                      </Badge>
                    ) : (
                      <span className="text-slate-500">Never run</span>
                    )}
                  </div>
                </div>
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Last totals</div>
                  <div className="text-slate-900">
                    {schedule.lastMatched} matched · {schedule.lastUpdated} updated
                  </div>
                </div>
              </div>

              {schedule.lastMessage ? (
                <p className="text-sm text-amber-600">{schedule.lastMessage}</p>
              ) : null}

              <div className="flex flex-wrap items-end gap-4">
                <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={effectiveEnabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  Run automatically
                </label>
                <div className="w-48">
                  <TextField
                    label="Every (minutes)"
                    type="number"
                    min={CATALYST_PORTAL_MIN_INTERVAL_MINUTES}
                    max={CATALYST_PORTAL_MAX_INTERVAL_MINUTES}
                    step={15}
                    value={effectiveInterval}
                    onChange={(e) => setInterval(e.target.value)}
                    disabled={!effectiveEnabled}
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={handleSaveSchedule}
                  isLoading={updateSchedule.isPending}
                >
                  Save schedule
                </Button>
                <Button
                  onClick={() => runSync.mutate({})}
                  isLoading={runSync.isPending && !runSync.variables?.customerIds}
                >
                  Run now
                </Button>
                <Button variant="ghost" onClick={() => setShowTest((open) => !open)}>
                  {showTest ? 'Hide test records' : 'Test with selected records'}
                </Button>
              </div>

              <p className="text-xs text-slate-500">
                Scheduled runs are checked every 15 minutes, so the interval is honoured to the
                nearest quarter hour. The minimum interval is{' '}
                {CATALYST_PORTAL_MIN_INTERVAL_MINUTES} minutes.
              </p>

              {scheduleError ? <p className="text-sm text-rose-600">{scheduleError}</p> : null}
              {scheduleSaved ? <p className="text-sm text-emerald-600">Schedule saved.</p> : null}
              {runError ? <p className="text-sm text-rose-600">{runError}</p> : null}
              {result ? <ResultSummary result={result} /> : null}
            </div>
          ) : null}
        </DataState>

        {showTest ? (
          <div className="space-y-3 border-t border-slate-200 pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-72">
                <TextField
                  label="Find customers"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name or NetSuite account ID"
                />
              </div>
              <Button
                variant="secondary"
                onClick={() => runSync.mutate({ customerIds: selected, dryRun: true })}
                disabled={selected.length === 0}
                isLoading={runSync.isPending && runSync.variables?.dryRun === true}
              >
                Preview selected
              </Button>
              <Button
                onClick={() => runSync.mutate({ customerIds: selected, dryRun: false })}
                disabled={selected.length === 0}
                isLoading={
                  runSync.isPending &&
                  Boolean(runSync.variables?.customerIds) &&
                  runSync.variables?.dryRun !== true
                }
              >
                Sync selected ({selected.length})
              </Button>
            </div>

            <DataState
              isLoading={candidatesQuery.isLoading}
              isError={candidatesQuery.isError}
              error={candidatesQuery.error}
              onRetry={() => void candidatesQuery.refetch()}
              isEmpty={candidates.length === 0}
              emptyTitle="No matching customers"
              emptyDescription="No customers with a NetSuite Account ID matched this search."
            >
              <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="w-10 px-3 py-2" />
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2">NetSuite Account ID</th>
                      <th className="px-3 py-2">Current name</th>
                      <th className="px-3 py-2">Portal name</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {candidates.map((row: CatalystPortalAccountMatch) => (
                      <tr key={row.customerId} className="hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedSet.has(row.customerId)}
                            onChange={(e) => toggle(row.customerId, e.target.checked)}
                          />
                        </td>
                        <td className="px-3 py-2 text-slate-900">{row.customerName}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">
                          {row.netsuiteAccountId}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.currentAccountName ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{row.portalAccountName ?? '—'}</td>
                        <td className="px-3 py-2">
                          {!row.matched ? (
                            <Badge tone="gray">Not in portal</Badge>
                          ) : row.changed ? (
                            <Badge tone="amber">Will change</Badge>
                          ) : (
                            <Badge tone="green">Current</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DataState>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
